import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Activity, Scale, X } from "lucide-react";
import { cn } from "../lib/utils";
import { WEIGHT_MANAGEMENT_SCORE_BANDS } from "../utils/weightManagementAboutContent";

type WeightManagementAboutSheetProps = {
  open: boolean;
  onClose: () => void;
};

const WeightManagementAboutSheet = ({
  open,
  onClose,
}: WeightManagementAboutSheetProps) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[250] flex flex-col justify-end" role="presentation">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      <div
        className="relative z-10 mx-auto w-full max-w-[400px] duration-200 animate-in slide-in-from-bottom-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wms-about-title"
      >
        <div className="flex max-h-[min(82dvh,720px)] flex-col overflow-hidden rounded-t-3xl border border-border bg-background shadow-2xl">
          {/* Fixed header — not inside scroll area */}
          <div className="shrink-0 border-b border-border/40 bg-background">
            <div className="flex justify-center pb-2 pt-3">
              <div className="h-1 w-11 rounded-full bg-muted-foreground/25" aria-hidden />
            </div>

            <div className="flex items-center justify-between gap-3 px-5 pb-4">
              <h2
                id="wms-about-title"
                className="text-lg font-bold text-foreground"
              >
                About this score
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:bg-muted/60"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Scrollable body only */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain scrollbar-hide">
            <div className="space-y-5 px-5 pb-[max(1.75rem,env(safe-area-inset-bottom,1.25rem))] pt-4">
              <section className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
                <div className="mb-3 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950/40">
                    <Scale className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="pt-1 text-base font-bold leading-snug text-foreground">
                    What is the Weight Management Score?
                  </h3>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  A single number{" "}
                  <span className="font-semibold text-foreground">(0–100)</span> that
                  measures how well your current habits are set up for healthy weight
                  management — not how much you weigh, but{" "}
                  <span className="font-semibold text-foreground">where you are headed</span>.
                </p>
                <div className="mt-3 rounded-xl border border-blue-200/60 bg-blue-50/80 px-3.5 py-3 dark:border-blue-800/30 dark:bg-blue-950/20">
                  <p className="text-sm leading-relaxed text-blue-800 dark:text-blue-200">
                    ☀️ Think of it like a{" "}
                    <span className="font-bold">weather forecast for your weight</span>.
                    It doesn&apos;t show what the weather is today — it shows where your
                    habits are pointing you over the{" "}
                    <span className="font-bold">next 4 weeks</span>.
                  </p>
                </div>
              </section>

              <section className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950/40">
                    <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="pt-1 text-base font-bold leading-snug text-foreground">
                    How to read your score
                  </h3>
                </div>

                <div className="space-y-2.5">
                  {WEIGHT_MANAGEMENT_SCORE_BANDS.map((band) => (
                    <div
                      key={band.range}
                      className={cn(
                        "rounded-xl border p-3.5",
                        band.bg,
                        band.border,
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", band.dot)}
                        />
                        <div className="min-w-0 flex-1">
                          <p className={cn("text-sm font-black tabular-nums", band.color)}>
                            {band.range}
                          </p>
                          <p className={cn("mt-0.5 text-sm font-semibold", band.color)}>
                            {band.label}
                          </p>
                          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                            {band.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WeightManagementAboutSheet;
